<template>
    <Modal :modal-title="modalTitle"
           :dialog="dialog"
           max-width="1600px"
           @close="close"
           @visibility="visibility($event)">
        <template slot="content">
            <v-row>
                <v-col cols="12">
                    <h3 class="font-weight-medium mb-3">{{ $t('labels.details') }}</h3>
                    <v-row>
                        <v-col md="12" sm="12" cols="12">
                            <v-sheet color="white" class="pa-4" elevation="1">
                                <v-layout align-stretch :wrap="isMobile">
                                    <v-flex sm6
                                            :class="{'bordered pr-4': smAndUp}">
                                        <InformationItem
                                            :title="$t('labels.name')"
                                            name="name"
                                            :text="sequenceEvaluationData.name"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.status')"
                                            name="status">
                                            <SequenceStatusChip :status="sequenceEvaluationData.status" />
                                        </InformationItem>
                                        <InformationItem
                                            :title="$t('labels.point')"
                                            name="point"
                                            :text="sequenceEvaluationData.point.name"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.assignedEmployees')"
                                            name="assignedEmployees"
                                            :text="assignedEmployees"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.employeeToNotify')"
                                            name="employeeToNotify"
                                            :text="sequenceEvaluationData.employeeToNotify.name"
                                        ></InformationItem>
                                    </v-flex>
                                    <v-flex sm6 :class="{'bordered pl-4': smAndUp}">
                                        <InformationItem
                                            :title="$t('labels.trackType')"
                                            name="type"
                                            :text="this.$t(TRACK_TYPE_TRANSLATE_KEYS[sequenceEvaluationData.sequence.type])"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.incidentType')"
                                            name="incidentType"
                                            :text="sequenceEvaluationData.incidentType.name"
                                        ></InformationItem>
                                        <InformationItem
                                            v-if="sequenceEvaluationData.inspectionTemplate && sequenceEvaluationData.inspectionTemplate.name"
                                            :title="$t('labels.inspectionTemplate')"
                                            name="inspectionTemplate"
                                            :text="sequenceEvaluationData.inspectionTemplate.name"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.total_duration')"
                                            name="durationTime"
                                            :text="timeToString(sequenceEvaluationData.sequence.durationTime)"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.start_datetime')"
                                            name="startDate"
                                            :text="PARSE_DATETIME(sequenceEvaluationData.startDateTime)"
                                        ></InformationItem>
                                        <InformationItem
                                            :title="$t('labels.end_datetime')"
                                            name="endDate"
                                            :text="PARSE_DATETIME(sequenceEvaluationData.endDateTime)"
                                        ></InformationItem>
                                    </v-flex>
                                </v-layout>
                            </v-sheet>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12">
                    <h3 class="font-weight-medium mb-3">{{ $t('labels.sequence_steps') }}</h3>
                    <v-sheet color="white" elevation="1">
                        <v-data-table
                            :items="sequenceEvaluationData.routeSteps"
                            :headers="headers"
                            disable-pagination
                            hide-default-footer
                        >
                            <template v-slot:item.order="{ item }">
                                <span>{{ item.order + 1 }}</span>
                            </template>
                            <template v-slot:item.name="{ item }">
                                <span>{{item.name}}</span>
                            </template>
                            <template v-slot:item.status="{ item }">
                                <SequenceStatusChip :status="item.status" />
                            </template>
                            <template v-slot:item.scanTime="{ item }">
                                <span>{{item.scanTime}}</span>
                            </template>
                        </v-data-table>
                    </v-sheet>
                </v-col>
            </v-row>
        </template>
    </Modal>
</template>

<script>
import Modal from '@/components/templates/Modal.vue';
import { mapState } from 'vuex';
import InformationItem from '@/components/customers/InformationItem.vue';
import {
    REPETITION_CYCLE_TRANSLATE_KEYS,
    REPETITION_PATTERN_TRANSLATE_KEYS,
    TRACK_TYPE_TRANSLATE_KEYS, WEEKDAYS_TRANSLATE_KEYS_ARRAY
} from '@/constants/cyclicRevision';
import SequenceStatusChip from '@/components/cyclicRevisions/SequenceStatusChip.vue';
import { PARSE_DATETIME } from '@/helpers/dates';

export default {
    name: 'SequenceEvaluationsDetails',
    data: () => ({
        PARSE_DATETIME
    }),
    components: {
        InformationItem,
        Modal,
        SequenceStatusChip
    },
    props: {
        dialog: {
            type: Boolean,
            required: true
        }
    },
    computed: {
        REPETITION_CYCLE_TRANSLATE_KEYS () {
            return REPETITION_CYCLE_TRANSLATE_KEYS;
        },
        REPETITION_PATTERN_TRANSLATE_KEYS () {
            return REPETITION_PATTERN_TRANSLATE_KEYS;
        },
        TRACK_TYPE_TRANSLATE_KEYS () {
            return TRACK_TYPE_TRANSLATE_KEYS;
        },
        ...mapState('manageSequenceEvaluation', [
            'sequenceEvaluationData'
        ]),
        modalTitle () {
            return this.$t('labels.track') + ' ' + this.sequenceEvaluationData.name;
        },
        isMobile () {
            return this.$vuetify.breakpoint.xs;
        },
        smAndUp () {
            return this.$vuetify.breakpoint.smAndUp;
        },
        headers () {
            return [
                {
                    text: this.$t('tracks.table.lp'),
                    value: 'order',
                    sortable: false
                },
                {
                    text: this.$t('tracks.table.title'),
                    value: 'name',
                    sortable: false
                },
                {
                    text: this.$t('tracks.table.status'),
                    value: 'status',
                    sortable: false
                },
                {
                    text: this.$t('tracks.table.scan_time'),
                    value: 'scanTime',
                    sortable: false
                }
            ];
        },
        assignedEmployees () {
            return this.sequenceEvaluationData.assignedEmployees.length > 0 ? this.sequenceEvaluationData.assignedEmployees.map(employee => employee.name).join(', ') : this.$t('tracks.any_assigned_employee');
        }
    },
    methods: {
        close () {
            this.$emit('close');
        },
        visibility (event) {
            this.$emit('visibility', event);
        },
        weekdaysToString (weekdays) {
            return weekdays.map(day => this.$t(WEEKDAYS_TRANSLATE_KEYS_ARRAY[day])).join(', ');
        },
        timeToString (time) {
            return time?.HH && time?.mm ? time.HH + ':' + time.mm : '--:--';
        }
    }
};
</script>

<style scoped lang="scss">
</style>
